import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Building2, Edit, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import DOMPurify from 'dompurify';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const FacilitiesUnitForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_name: '',
    operational_period_from: '',
    operational_period_to: '',
    facilities_unit_officer: '',
    // Planning Section Information
    iap_details: '',
    incident_objectives: '',
    priorities: '',
    incident_size_scope: '',
    projected_duration: '',
    resource_status: '',
    // Command Information
    ic_requirements: '',
    facility_identification: '',
    facility_location: '',
    safety_hazards: '',
    safety_protocols: '',
    // Finance Information
    financial_guidelines: '',
    contract_information: '',
    cost_tracking_notes: '',
    // Operations Information
    operational_needs: '',
    decontamination_areas: '',
    helibase_requirements: '',
    staging_area_needs: '',
    // Logistics Units Information
    supply_support_items: '',
    communications_equipment: '',
    food_service_space: '',
    vehicle_parking_notes: '',
    // Site-Specific Information
    facility_records: [],
    utility_records: [],
    security_records: [],
    // Prepared By
    prepared_by: '',
    signature: '',
    date_time_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [cardSaveStatus, setCardSaveStatus] = useState({}); // Track save status per card
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Save a specific card's data
  const saveCard = async (cardName) => {
    setCardSaveStatus(prev => ({ ...prev, [cardName]: 'saving' }));
    try {
      await saveEntry();
      setCardSaveStatus(prev => ({ ...prev, [cardName]: 'saved' }));
      setTimeout(() => {
        setCardSaveStatus(prev => ({ ...prev, [cardName]: null }));
      }, 3000);
    } catch (error) {
      setCardSaveStatus(prev => ({ ...prev, [cardName]: 'error' }));
      setTimeout(() => {
        setCardSaveStatus(prev => ({ ...prev, [cardName]: null }));
      }, 5000);
    }
  };

  // Render save status indicator
  const renderSaveStatus = (cardName) => {
    const status = cardSaveStatus[cardName];
    if (status === 'saving') return <span className="text-yellow-500 text-sm ml-2">Saving...</span>;
    if (status === 'saved') return <span className="text-green-500 text-sm ml-2">✓ Saved</span>;
    if (status === 'error') return <span className="text-red-500 text-sm ml-2">Save failed</span>;
    return null;
  };

  // Fetch all Facilities Unit entries for current exercise
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/facilities-unit?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching Facilities Unit entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        incident_name: editingEntry.incident_name || '',
        operational_period_from: editingEntry.operational_period_from || '',
        operational_period_to: editingEntry.operational_period_to || '',
        facilities_unit_officer: editingEntry.facilities_unit_officer || '',
        iap_details: editingEntry.iap_details || '',
        incident_objectives: editingEntry.incident_objectives || '',
        priorities: editingEntry.priorities || '',
        incident_size_scope: editingEntry.incident_size_scope || '',
        projected_duration: editingEntry.projected_duration || '',
        resource_status: editingEntry.resource_status || '',
        ic_requirements: editingEntry.ic_requirements || '',
        facility_identification: editingEntry.facility_identification || '',
        facility_location: editingEntry.facility_location || '',
        safety_hazards: editingEntry.safety_hazards || '',
        safety_protocols: editingEntry.safety_protocols || '',
        financial_guidelines: editingEntry.financial_guidelines || '',
        contract_information: editingEntry.contract_information || '',
        cost_tracking_notes: editingEntry.cost_tracking_notes || '',
        operational_needs: editingEntry.operational_needs || '',
        decontamination_areas: editingEntry.decontamination_areas || '',
        helibase_requirements: editingEntry.helibase_requirements || '',
        staging_area_needs: editingEntry.staging_area_needs || '',
        supply_support_items: editingEntry.supply_support_items || '',
        communications_equipment: editingEntry.communications_equipment || '',
        food_service_space: editingEntry.food_service_space || '',
        vehicle_parking_notes: editingEntry.vehicle_parking_notes || '',
        facility_records: editingEntry.facility_records || [],
        utility_records: editingEntry.utility_records || [],
        security_records: editingEntry.security_records || [],
        prepared_by: editingEntry.prepared_by || '',
        signature: editingEntry.signature || '',
        date_time_prepared: editingEntry.date_time_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_name: '',
        operational_period_from: '',
        operational_period_to: '',
        facilities_unit_officer: '',
        iap_details: '',
        incident_objectives: '',
        priorities: '',
        incident_size_scope: '',
        projected_duration: '',
        resource_status: '',
        ic_requirements: '',
        facility_identification: '',
        facility_location: '',
        safety_hazards: '',
        safety_protocols: '',
        financial_guidelines: '',
        contract_information: '',
        cost_tracking_notes: '',
        operational_needs: '',
        decontamination_areas: '',
        helibase_requirements: '',
        staging_area_needs: '',
        supply_support_items: '',
        communications_equipment: '',
        food_service_space: '',
        vehicle_parking_notes: '',
        facility_records: [],
        utility_records: [],
        security_records: [],
        prepared_by: '',
        signature: '',
        date_time_prepared: ''
      });
    }
  }, [editingEntry, currentExercise]);

  // Facility Record functions
  const addFacilityRecord = () => {
    const newRecord = {
      id: Date.now().toString(),
      facility_name: '',
      facility_type: '',
      location: '',
      capacity: '',
      current_condition: '',
      layout_notes: '',
      setup_date: '',
      demobilization_date: '',
      status: ''
    };
    setFormData(prev => ({
      ...prev,
      facility_records: [...prev.facility_records, newRecord]
    }));
  };

  const updateFacilityRecord = (index, field, value) => {
    const updated = [...formData.facility_records];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, facility_records: updated }));
  };

  const deleteFacilityRecord = (index) => {
    setFormData(prev => ({
      ...prev,
      facility_records: prev.facility_records.filter((_, i) => i !== index)
    }));
  };

  // Utility Record functions
  const addUtilityRecord = () => {
    const newRecord = {
      id: Date.now().toString(),
      utility_type: '',
      provider: '',
      availability: '',
      capacity: '',
      connection_date: '',
      location: '',
      contact_info: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      utility_records: [...prev.utility_records, newRecord]
    }));
  };

  const updateUtilityRecord = (index, field, value) => {
    const updated = [...formData.utility_records];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, utility_records: updated }));
  };

  const deleteUtilityRecord = (index) => {
    setFormData(prev => ({
      ...prev,
      utility_records: prev.utility_records.filter((_, i) => i !== index)
    }));
  };

  // Security Record functions
  const addSecurityRecord = () => {
    const newRecord = {
      id: Date.now().toString(),
      security_measure: '',
      location: '',
      responsible_person: '',
      implementation_date: '',
      review_date: '',
      status: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      security_records: [...prev.security_records, newRecord]
    }));
  };

  const updateSecurityRecord = (index, field, value) => {
    const updated = [...formData.security_records];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, security_records: updated }));
  };

  const deleteSecurityRecord = (index) => {
    setFormData(prev => ({
      ...prev,
      security_records: prev.security_records.filter((_, i) => i !== index)
    }));
  };

  // Save Facilities Unit entry
  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/facilities-unit/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/facilities-unit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingEntry ? 'Facilities Unit entry updated successfully!' : 'Facilities Unit entry created successfully!');
        fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        alert('Failed to save Facilities Unit entry');
      }
    } catch (error) {
      console.error('Error saving Facilities Unit entry:', error);
      alert('Error saving Facilities Unit entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete Facilities Unit entry
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this Facilities Unit entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/facilities-unit/${entryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Facilities Unit entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete Facilities Unit entry');
      }
    } catch (error) {
      console.error('Error deleting Facilities Unit entry:', error);
      alert('Error deleting Facilities Unit entry');
    } finally {
      setLoading(false);
    }
  };

  // Print All Facilities Report
  const handlePrintAllFacilitiesReport = () => {
    const printWindow = window.open('', '_blank');
    
    // Use saved entries if available, otherwise use current form data
    const printEntries = entries.length > 0 ? entries : [formData];
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Facilities Unit Report - ${currentExercise?.exercise_name || 'EXRSIM'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              line-height: 1.6;
            }
            h1 {
              color: #f97316;
              text-align: center;
              border-bottom: 3px solid #f97316;
              padding-bottom: 10px;
            }
            h2 {
              color: #f97316;
              margin-top: 30px;
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 5px;
            }
            h3 {
              color: #374151;
              margin-top: 20px;
            }
            .entry-container {
              margin-bottom: 50px;
              page-break-after: always;
            }
            .section {
              margin-bottom: 30px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              margin: 15px 0;
            }
            .info-item {
              padding: 10px;
              background: #f9fafb;
              border-left: 3px solid #f97316;
            }
            .info-label {
              font-weight: bold;
              color: #6b7280;
              font-size: 12px;
            }
            .info-value {
              color: #111827;
              margin-top: 3px;
            }
            .record-card {
              border: 1px solid #e5e7eb;
              padding: 15px;
              margin: 10px 0;
              background: #f9fafb;
              border-radius: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
            }
            th, td {
              border: 1px solid #e5e7eb;
              padding: 10px;
              text-align: left;
            }
            th {
              background: #f3f4f6;
              font-weight: bold;
              color: #374151;
            }
            @media print {
              body { padding: 10px; }
              h1 { font-size: 20px; }
              h2 { font-size: 16px; page-break-after: avoid; }
              h3 { font-size: 14px; page-break-after: avoid; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Facilities Unit Report</h1>
          <p style="text-align: center; color: #6b7280; margin-bottom: 30px;">
            Exercise: ${currentExercise?.exercise_name || 'N/A'}<br>
            Generated on ${new Date().toLocaleString()}
          </p>

          ${printEntries.map((entry, entryIndex) => `
            <div class="entry-container">
              <h2>Entry #${entryIndex + 1}: ${entry.incident_name || 'Facilities Unit Entry'}</h2>
              
              <!-- 1. Basic Information -->
              <div class="section">
                <h3>1. Basic Information</h3>
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Exercise Name</div>
                    <div class="info-value">${entry.exercise_name || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Incident Name</div>
                    <div class="info-value">${entry.incident_name || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Facilities Unit Officer</div>
                    <div class="info-value">${entry.facilities_unit_officer || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Operational Period</div>
                    <div class="info-value">${entry.operational_period_from || 'N/A'} to ${entry.operational_period_to || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <!-- 2. Planning Section Information -->
              <div class="section">
                <h3>2. Planning Section Information</h3>
                <div class="info-item">
                  <div class="info-label">IAP Details</div>
                  <div class="info-value">${entry.iap_details || 'N/A'}</div>
                </div>
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Incident Objectives</div>
                    <div class="info-value">${entry.incident_objectives || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Priorities</div>
                    <div class="info-value">${entry.priorities || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Incident Size & Scope</div>
                    <div class="info-value">${entry.incident_size_scope || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Projected Duration</div>
                    <div class="info-value">${entry.projected_duration || 'N/A'}</div>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">Resource Status</div>
                  <div class="info-value">${entry.resource_status || 'N/A'}</div>
                </div>
              </div>

              <!-- 3. Command Information -->
              <div class="section">
                <h3>3. Command Information</h3>
                <div class="info-item">
                  <div class="info-label">Incident Commander Requirements</div>
                  <div class="info-value">${entry.ic_requirements || 'N/A'}</div>
                </div>
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Facility Identification</div>
                    <div class="info-value">${entry.facility_identification || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Facility Location</div>
                    <div class="info-value">${entry.facility_location || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Safety Hazards</div>
                    <div class="info-value">${entry.safety_hazards || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Safety Protocols</div>
                    <div class="info-value">${entry.safety_protocols || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <!-- 4. Finance/Administration Information -->
              <div class="section">
                <h3>4. Finance/Administration Information</h3>
                <div class="info-item">
                  <div class="info-label">Financial Guidelines</div>
                  <div class="info-value">${entry.financial_guidelines || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Contract Information</div>
                  <div class="info-value">${entry.contract_information || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Cost Tracking Notes</div>
                  <div class="info-value">${entry.cost_tracking_notes || 'N/A'}</div>
                </div>
              </div>

              <!-- 5. Operations Information -->
              <div class="section">
                <h3>5. Operations Information</h3>
                <div class="info-item">
                  <div class="info-label">Operational Needs</div>
                  <div class="info-value">${entry.operational_needs || 'N/A'}</div>
                </div>
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Decontamination Areas</div>
                    <div class="info-value">${entry.decontamination_areas || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Helibase Requirements</div>
                    <div class="info-value">${entry.helibase_requirements || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Staging Area Needs</div>
                    <div class="info-value">${entry.staging_area_needs || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <!-- 6. Logistics Units Coordination -->
              <div class="section">
                <h3>6. Logistics Units Coordination</h3>
                <div class="info-item">
                  <div class="info-label">Supply Support Items</div>
                  <div class="info-value">${entry.supply_support_items || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Communications Equipment</div>
                  <div class="info-value">${entry.communications_equipment || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Food Service Space Requirements</div>
                  <div class="info-value">${entry.food_service_space || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Vehicle Parking & Traffic Flow</div>
                  <div class="info-value">${entry.vehicle_parking_notes || 'N/A'}</div>
                </div>
              </div>

              <!-- 7. Facility Infrastructure Records -->
              <div class="section">
                <h3>7. Facility Infrastructure Records</h3>
                ${entry.facility_records && entry.facility_records.length > 0 ? `
                  ${entry.facility_records.map((fac, i) => `
                    <div class="record-card">
                      <h4 style="margin: 0 0 10px 0; color: #374151;">Facility #${i + 1}: ${fac.facility_name || 'Unnamed'}</h4>
                      <table>
                        <tr><th>Facility Type</th><td>${fac.facility_type || 'N/A'}</td></tr>
                        <tr><th>Location</th><td>${fac.location || 'N/A'}</td></tr>
                        <tr><th>Capacity</th><td>${fac.capacity || 'N/A'}</td></tr>
                        <tr><th>Current Condition</th><td>${fac.current_condition || 'N/A'}</td></tr>
                        <tr><th>Status</th><td>${fac.status || 'N/A'}</td></tr>
                        <tr><th>Setup Date</th><td>${fac.setup_date || 'N/A'}</td></tr>
                        <tr><th>Demobilization Date</th><td>${fac.demobilization_date || 'N/A'}</td></tr>
                        <tr><th>Layout Notes</th><td>${fac.layout_notes || 'N/A'}</td></tr>
                      </table>
                    </div>
                  `).join('')}
                ` : '<p style="color: #6b7280;">No facility records</p>'}
              </div>

              <!-- 8. Utilities & Services -->
              <div class="section">
                <h3>8. Utilities & Services</h3>
                ${entry.utility_records && entry.utility_records.length > 0 ? `
                  ${entry.utility_records.map((util, i) => `
                    <div class="record-card">
                      <h4 style="margin: 0 0 10px 0; color: #374151;">Utility #${i + 1}: ${util.utility_type || 'Unnamed'}</h4>
                      <table>
                        <tr><th>Provider</th><td>${util.provider || 'N/A'}</td></tr>
                        <tr><th>Availability</th><td>${util.availability || 'N/A'}</td></tr>
                        <tr><th>Capacity</th><td>${util.capacity || 'N/A'}</td></tr>
                        <tr><th>Connection Date</th><td>${util.connection_date || 'N/A'}</td></tr>
                        <tr><th>Location</th><td>${util.location || 'N/A'}</td></tr>
                        <tr><th>Contact Info</th><td>${util.contact_info || 'N/A'}</td></tr>
                        <tr><th>Notes</th><td>${util.notes || 'N/A'}</td></tr>
                      </table>
                    </div>
                  `).join('')}
                ` : '<p style="color: #6b7280;">No utility records</p>'}
              </div>

              <!-- 9. Security Requirements -->
              <div class="section">
                <h3>9. Security Requirements</h3>
                ${entry.security_records && entry.security_records.length > 0 ? `
                  ${entry.security_records.map((sec, i) => `
                    <div class="record-card">
                      <h4 style="margin: 0 0 10px 0; color: #374151;">Security Measure #${i + 1}: ${sec.security_measure || 'Unnamed'}</h4>
                      <table>
                        <tr><th>Location</th><td>${sec.location || 'N/A'}</td></tr>
                        <tr><th>Responsible Person</th><td>${sec.responsible_person || 'N/A'}</td></tr>
                        <tr><th>Status</th><td>${sec.status || 'N/A'}</td></tr>
                        <tr><th>Implementation Date</th><td>${sec.implementation_date || 'N/A'}</td></tr>
                        <tr><th>Review Date</th><td>${sec.review_date || 'N/A'}</td></tr>
                        <tr><th>Notes</th><td>${sec.notes || 'N/A'}</td></tr>
                      </table>
                    </div>
                  `).join('')}
                ` : '<p style="color: #6b7280;">No security records</p>'}
              </div>

              <!-- 10. Prepared By -->
              <div class="section">
                <h3>10. Prepared By</h3>
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Prepared By</div>
                    <div class="info-value">${entry.prepared_by || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Signature</div>
                    <div class="info-value">${entry.signature || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Date/Time Prepared</div>
                    <div class="info-value">${entry.date_time_prepared || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          `).join('')}

          <div style="text-align: center; color: #6b7280; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p>Facilities Unit Report - ${currentExercise?.exercise_name || 'EXRSIM'}</p>
            <p style="font-size: 12px;">This is an automatically generated report</p>
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Print individual entry (reuses the comprehensive print template scoped to one entry)
  const handlePrintEntry = (entry) => {
    const prevFormData = formData;
    setFormData(entry);
    setTimeout(() => {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setFormData(prevFormData);
        return;
      }
      // Build print content using the entry directly to avoid waiting on state
      const data = entry || {};
      const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
      const incidentName = data.incident_name || 'N/A';
      const generated = new Date().toLocaleString();

      const tableSection = (title, columns, rows, emptyMsg) => {
        if (!rows || rows.length === 0) {
          return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
        }
        const head = columns.map(c => `<th>${c.label}</th>`).join('');
        const body = rows.map(r =>
          `<tr>${columns.map(c => `<td>${r[c.key] != null && r[c.key] !== '' ? r[c.key] : ''}</td>`).join('')}</tr>`
        ).join('');
        return `
          <div class="section">
            <h2>${title} <span class="count">(${rows.length})</span></h2>
            <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
          </div>`;
      };

      const infoItem = (label, value) =>
        `<div class="info-item"><div class="info-label">${label}</div><div class="info-value">${value || 'N/A'}</div></div>`;

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Facilities Unit Report - ${exerciseName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
              h1 { color: #3b82f6; text-align: center; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 6px; }
              .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
              h2 { color: #3b82f6; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
              .count { color: #6b7280; font-size: 13px; font-weight: normal; }
              .section { margin-bottom: 22px; page-break-inside: avoid; }
              .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
              .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
              .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 10px 0; }
              .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #3b82f6; }
              .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
              .info-value { color: #111827; margin-top: 3px; font-size: 13px; white-space: pre-wrap; }
              table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
              th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
              th { background: #eff6ff; font-weight: bold; color: #1e40af; }
              tr:nth-child(even) { background: #f9fafb; }
              .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
              .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
              @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
            </style>
          </head>
          <body>
            <h1>Facilities Unit Report</h1>
            <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

            <div class="section">
              <h2>Basic Information</h2>
              <div class="info-grid">
                ${infoItem('Exercise Name', exerciseName)}
                ${infoItem('Incident Name', incidentName)}
                ${infoItem('Operational Period From', data.operational_period_from)}
                ${infoItem('Operational Period To', data.operational_period_to)}
                ${infoItem('Facilities Unit Officer', data.facilities_unit_officer)}
              </div>
            </div>

            <div class="section">
              <h2>Planning Section Information</h2>
              <div class="info-grid-1">
                ${infoItem('IAP Details', data.iap_details)}
                ${infoItem('Incident Objectives', data.incident_objectives)}
                ${infoItem('Priorities', data.priorities)}
                ${infoItem('Incident Size &amp; Scope', data.incident_size_scope)}
                ${infoItem('Projected Duration', data.projected_duration)}
                ${infoItem('Resource Status', data.resource_status)}
              </div>
            </div>

            <div class="section">
              <h2>Command Information</h2>
              <div class="info-grid-1">
                ${infoItem('IC Requirements', data.ic_requirements)}
                ${infoItem('Facility Identification', data.facility_identification)}
                ${infoItem('Facility Location', data.facility_location)}
                ${infoItem('Safety Hazards', data.safety_hazards)}
                ${infoItem('Safety Protocols', data.safety_protocols)}
              </div>
            </div>

            <div class="section">
              <h2>Finance Information</h2>
              <div class="info-grid-1">
                ${infoItem('Financial Guidelines', data.financial_guidelines)}
                ${infoItem('Contract Information', data.contract_information)}
                ${infoItem('Cost Tracking Notes', data.cost_tracking_notes)}
              </div>
            </div>

            <div class="section">
              <h2>Operations Information</h2>
              <div class="info-grid-1">
                ${infoItem('Operational Needs', data.operational_needs)}
                ${infoItem('Decontamination Areas', data.decontamination_areas)}
                ${infoItem('Helibase Requirements', data.helibase_requirements)}
                ${infoItem('Staging Area Needs', data.staging_area_needs)}
              </div>
            </div>

            <div class="section">
              <h2>Logistics Units Information</h2>
              <div class="info-grid-1">
                ${infoItem('Supply Support Items', data.supply_support_items)}
                ${infoItem('Communications Equipment', data.communications_equipment)}
                ${infoItem('Food Service Space', data.food_service_space)}
                ${infoItem('Vehicle Parking Notes', data.vehicle_parking_notes)}
              </div>
            </div>

            ${tableSection('Facility Records',
              [
                { key: 'facility_name', label: 'Facility Name' },
                { key: 'facility_type', label: 'Type' },
                { key: 'location', label: 'Location' },
                { key: 'capacity', label: 'Capacity' },
                { key: 'current_condition', label: 'Condition' },
                { key: 'layout_notes', label: 'Layout Notes' },
                { key: 'setup_date', label: 'Setup Date' },
                { key: 'demobilization_date', label: 'Demob Date' },
                { key: 'status', label: 'Status' }
              ],
              data.facility_records,
              'No facility records.')}

            ${tableSection('Utility Records',
              [
                { key: 'utility_type', label: 'Utility Type' },
                { key: 'provider', label: 'Provider' },
                { key: 'availability', label: 'Availability' },
                { key: 'capacity', label: 'Capacity' },
                { key: 'connection_date', label: 'Connection Date' },
                { key: 'location', label: 'Location' },
                { key: 'contact_info', label: 'Contact Info' },
                { key: 'notes', label: 'Notes' }
              ],
              data.utility_records,
              'No utility records.')}

            ${tableSection('Security Records',
              [
                { key: 'security_measure', label: 'Security Measure' },
                { key: 'location', label: 'Location' },
                { key: 'responsible_person', label: 'Responsible Person' },
                { key: 'implementation_date', label: 'Implementation' },
                { key: 'review_date', label: 'Review Date' },
                { key: 'status', label: 'Status' },
                { key: 'notes', label: 'Notes' }
              ],
              data.security_records,
              'No security records.')}

            <div class="section">
              <h2>Prepared By</h2>
              <div class="info-grid-3">
                ${infoItem('Prepared By', data.prepared_by)}
                ${infoItem('Signature', data.signature)}
                ${infoItem('Date/Time Prepared', data.date_time_prepared)}
              </div>
            </div>

            <div class="footer">
              <div><strong>Facilities Unit Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
              <div>Powered by EXRSIM</div>
            </div>
          </body>
        </html>
      `;

      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 250);
      // Restore prior form state
      setFormData(prevFormData);
    }, 50);
  };

  // Simple print function for form view
  const handlePrint = () => {
    const data = formData || {};
    const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
    const incidentName = data.incident_name || 'N/A';
    const generated = new Date().toLocaleString();

    const tableSection = (title, columns, rows, emptyMsg) => {
      if (!rows || rows.length === 0) {
        return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
      }
      const head = columns.map(c => `<th>${c.label}</th>`).join('');
      const body = rows.map(r =>
        `<tr>${columns.map(c => `<td>${r[c.key] != null && r[c.key] !== '' ? r[c.key] : ''}</td>`).join('')}</tr>`
      ).join('');
      return `
        <div class="section">
          <h2>${title} <span class="count">(${rows.length})</span></h2>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </div>`;
    };

    const infoItem = (label, value) =>
      `<div class="info-item"><div class="info-label">${label}</div><div class="info-value">${value || 'N/A'}</div></div>`;

    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Facilities Unit Report - ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
            h1 { color: #3b82f6; text-align: center; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 6px; }
            .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
            h2 { color: #3b82f6; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
            .count { color: #6b7280; font-size: 13px; font-weight: normal; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 10px 0; }
            .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #3b82f6; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
            .info-value { color: #111827; margin-top: 3px; font-size: 13px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #eff6ff; font-weight: bold; color: #1e40af; }
            tr:nth-child(even) { background: #f9fafb; }
            .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
            .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
            @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Facilities Unit Report</h1>
          <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

          <div class="section">
            <h2>Basic Information</h2>
            <div class="info-grid">
              ${infoItem('Exercise Name', exerciseName)}
              ${infoItem('Incident Name', incidentName)}
              ${infoItem('Operational Period From', data.operational_period_from)}
              ${infoItem('Operational Period To', data.operational_period_to)}
              ${infoItem('Facilities Unit Officer', data.facilities_unit_officer)}
            </div>
          </div>

          <div class="section">
            <h2>Planning Section Information</h2>
            <div class="info-grid-1">
              ${infoItem('IAP Details', data.iap_details)}
              ${infoItem('Incident Objectives', data.incident_objectives)}
              ${infoItem('Priorities', data.priorities)}
              ${infoItem('Incident Size &amp; Scope', data.incident_size_scope)}
              ${infoItem('Projected Duration', data.projected_duration)}
              ${infoItem('Resource Status', data.resource_status)}
            </div>
          </div>

          <div class="section">
            <h2>Command Information</h2>
            <div class="info-grid-1">
              ${infoItem('IC Requirements', data.ic_requirements)}
              ${infoItem('Facility Identification', data.facility_identification)}
              ${infoItem('Facility Location', data.facility_location)}
              ${infoItem('Safety Hazards', data.safety_hazards)}
              ${infoItem('Safety Protocols', data.safety_protocols)}
            </div>
          </div>

          <div class="section">
            <h2>Finance Information</h2>
            <div class="info-grid-1">
              ${infoItem('Financial Guidelines', data.financial_guidelines)}
              ${infoItem('Contract Information', data.contract_information)}
              ${infoItem('Cost Tracking Notes', data.cost_tracking_notes)}
            </div>
          </div>

          <div class="section">
            <h2>Operations Information</h2>
            <div class="info-grid-1">
              ${infoItem('Operational Needs', data.operational_needs)}
              ${infoItem('Decontamination Areas', data.decontamination_areas)}
              ${infoItem('Helibase Requirements', data.helibase_requirements)}
              ${infoItem('Staging Area Needs', data.staging_area_needs)}
            </div>
          </div>

          <div class="section">
            <h2>Logistics Units Information</h2>
            <div class="info-grid-1">
              ${infoItem('Supply Support Items', data.supply_support_items)}
              ${infoItem('Communications Equipment', data.communications_equipment)}
              ${infoItem('Food Service Space', data.food_service_space)}
              ${infoItem('Vehicle Parking Notes', data.vehicle_parking_notes)}
            </div>
          </div>

          ${tableSection('Facility Records',
            [
              { key: 'facility_name', label: 'Facility Name' },
              { key: 'facility_type', label: 'Type' },
              { key: 'location', label: 'Location' },
              { key: 'capacity', label: 'Capacity' },
              { key: 'current_condition', label: 'Condition' },
              { key: 'layout_notes', label: 'Layout Notes' },
              { key: 'setup_date', label: 'Setup Date' },
              { key: 'demobilization_date', label: 'Demob Date' },
              { key: 'status', label: 'Status' }
            ],
            data.facility_records,
            'No facility records.')}

          ${tableSection('Utility Records',
            [
              { key: 'utility_type', label: 'Utility Type' },
              { key: 'provider', label: 'Provider' },
              { key: 'availability', label: 'Availability' },
              { key: 'capacity', label: 'Capacity' },
              { key: 'connection_date', label: 'Connection Date' },
              { key: 'location', label: 'Location' },
              { key: 'contact_info', label: 'Contact Info' },
              { key: 'notes', label: 'Notes' }
            ],
            data.utility_records,
            'No utility records.')}

          ${tableSection('Security Records',
            [
              { key: 'security_measure', label: 'Security Measure' },
              { key: 'location', label: 'Location' },
              { key: 'responsible_person', label: 'Responsible Person' },
              { key: 'implementation_date', label: 'Implementation' },
              { key: 'review_date', label: 'Review Date' },
              { key: 'status', label: 'Status' },
              { key: 'notes', label: 'Notes' }
            ],
            data.security_records,
            'No security records.')}

          <div class="section">
            <h2>Prepared By</h2>
            <div class="info-grid-3">
              ${infoItem('Prepared By', data.prepared_by)}
              ${infoItem('Signature', data.signature)}
              ${infoItem('Date/Time Prepared', data.date_time_prepared)}
            </div>
          </div>

          <div class="footer">
            <div><strong>Facilities Unit Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
            <div>Powered by EXRSIM</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Facilities Unit</h1>
            <p className={`${theme.colors.textMuted}`}>Manage facility setup, maintenance, and demobilization operations</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrintAllFacilitiesReport} className="bg-blue-500 hover:bg-blue-600">
              <Printer className="h-4 w-4 mr-2" />
              Print Facilities Report
            </Button>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-2" />
              New Entry
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No Facilities Unit entries found</p>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              Create First Entry
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>
                        {entry.incident_name || 'Facilities Unit Entry'}
                      </h3>
                      <div className={`text-sm ${theme.colors.textMuted} space-y-1`}>
                        <p>Facilities Unit Officer: {entry.facilities_unit_officer || 'N/A'}</p>
                        <p>Operational Period: {entry.operational_period_from && entry.operational_period_to 
                          ? `${entry.operational_period_from} to ${entry.operational_period_to}` 
                          : 'N/A'}</p>
                        <p>Facilities: {entry.facility_records?.length || 0} | Utilities: {entry.utility_records?.length || 0} | Security: {entry.security_records?.length || 0}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => {
                          setEditingEntry(entry);
                          setView('form');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handlePrintEntry(entry)}
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-400 hover:bg-green-500/10"
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                      <Button
                        onClick={() => deleteEntry(entry.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form view
  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center print:hidden">
        <Button onClick={() => {
          setView('list');
          setEditingEntry(null);
        }} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button onClick={handlePrint} className="bg-blue-500 hover:bg-blue-600">
          <Printer className="h-4 w-4 mr-2" />
          Print Facilities Unit Report
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Facilities Unit</h1>
          <p className={`${theme.colors.textMuted}`}>Logistics Section - Support Branch</p>
        </div>

        {/* Basic Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600`}>
                  <span className={`${theme.colors.textPrimary}`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Enter incident name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period From</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_from ? formData.operational_period_from.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_from: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_from ? formData.operational_period_from.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_from: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period To</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_to ? formData.operational_period_to.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_to: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_to ? formData.operational_period_to.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_to: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Facilities Unit Officer</Label>
              <Input
                value={formData.facilities_unit_officer}
                onChange={(e) => setFormData(prev => ({ ...prev, facilities_unit_officer: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Enter officer name"
              />
            </div>
          </CardContent>
        </Card>

        {/* Planning Section Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Planning Section Information</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('planning')}
              <Button onClick={() => saveCard('planning')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Action Plan (IAP) Details</Label>
              <Textarea
                value={formData.iap_details}
                onChange={(e) => setFormData(prev => ({ ...prev, iap_details: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Core document details: operational period, objectives, priorities, assignments..."
                rows={3}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Objectives</Label>
              <Textarea
                value={formData.incident_objectives}
                onChange={(e) => setFormData(prev => ({ ...prev, incident_objectives: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Overall incident objectives..."
                rows={2}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Priorities</Label>
              <Textarea
                value={formData.priorities}
                onChange={(e) => setFormData(prev => ({ ...prev, priorities: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Priority list..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Size & Scope</Label>
                <Textarea
                  value={formData.incident_size_scope}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_size_scope: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Size, scope, and extent..."
                  rows={2}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Projected Duration</Label>
                <Input
                  value={formData.projected_duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, projected_duration: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="e.g., 7 days"
                />
              </div>
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Resource Status</Label>
              <Textarea
                value={formData.resource_status}
                onChange={(e) => setFormData(prev => ({ ...prev, resource_status: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Personnel and equipment status, space and service requirements..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Command Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Command Information</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('command')}
              <Button onClick={() => saveCard('command')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Commander Requirements</Label>
              <Textarea
                value={formData.ic_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, ic_requirements: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="IC direction regarding facility needs..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Facility Identification</Label>
                <Textarea
                  value={formData.facility_identification}
                  onChange={(e) => setFormData(prev => ({ ...prev, facility_identification: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Specific facilities identified..."
                  rows={2}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Facility Location</Label>
                <Textarea
                  value={formData.facility_location}
                  onChange={(e) => setFormData(prev => ({ ...prev, facility_location: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Location details..."
                  rows={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Safety Hazards</Label>
                <Textarea
                  value={formData.safety_hazards}
                  onChange={(e) => setFormData(prev => ({ ...prev, safety_hazards: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Identified hazards and risks..."
                  rows={2}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Safety Protocols</Label>
                <Textarea
                  value={formData.safety_protocols}
                  onChange={(e) => setFormData(prev => ({ ...prev, safety_protocols: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Required safety protocols..."
                  rows={2}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Finance Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Finance/Administration Information</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('finance')}
              <Button onClick={() => saveCard('finance')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Financial Guidelines</Label>
              <Textarea
                value={formData.financial_guidelines}
                onChange={(e) => setFormData(prev => ({ ...prev, financial_guidelines: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Budget limitations, approval processes..."
                rows={2}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Contract Information</Label>
              <Textarea
                value={formData.contract_information}
                onChange={(e) => setFormData(prev => ({ ...prev, contract_information: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Contracts for services..."
                rows={2}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Cost Tracking Notes</Label>
              <Textarea
                value={formData.cost_tracking_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, cost_tracking_notes: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Cost tracking details for facilities and maintenance..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Operations Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Operations Information</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('operations')}
              <Button onClick={() => saveCard('operations')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Needs</Label>
              <Textarea
                value={formData.operational_needs}
                onChange={(e) => setFormData(prev => ({ ...prev, operational_needs: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Day-to-day needs of field personnel..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Decontamination Areas</Label>
                <Input
                  value={formData.decontamination_areas}
                  onChange={(e) => setFormData(prev => ({ ...prev, decontamination_areas: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Decon area requirements"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Helibase Requirements</Label>
                <Input
                  value={formData.helibase_requirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, helibase_requirements: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Helibase needs"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Staging Area Needs</Label>
                <Input
                  value={formData.staging_area_needs}
                  onChange={(e) => setFormData(prev => ({ ...prev, staging_area_needs: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Staging area requirements"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logistics Units Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Logistics Units Coordination</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('logistics')}
              <Button onClick={() => saveCard('logistics')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Supply Support Items</Label>
              <Textarea
                value={formData.supply_support_items}
                onChange={(e) => setFormData(prev => ({ ...prev, supply_support_items: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Portable toilets, lighting, shower facilities..."
                rows={2}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Communications Equipment</Label>
              <Textarea
                value={formData.communications_equipment}
                onChange={(e) => setFormData(prev => ({ ...prev, communications_equipment: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Communication equipment, internet and telephone services..."
                rows={2}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Food Service Space Requirements</Label>
              <Textarea
                value={formData.food_service_space}
                onChange={(e) => setFormData(prev => ({ ...prev, food_service_space: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Space for cooking and food service areas..."
                rows={2}
              />
            </div>

            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Vehicle Parking & Traffic Flow</Label>
              <Textarea
                value={formData.vehicle_parking_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, vehicle_parking_notes: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
                placeholder="Transportation and support vehicle planning..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Facility Infrastructure Records Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Facility Infrastructure Records</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('infrastructure')}
              <Button onClick={() => saveCard('infrastructure')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Facility Records</Label>
              <Button onClick={addFacilityRecord} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Facility
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.facility_records.map((record, index) => (
                <Card key={record.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Facility Name</Label>
                        <Input
                          value={record.facility_name}
                          onChange={(e) => updateFacilityRecord(index, 'facility_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Building/structure name"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Facility Type</Label>
                        <select
                          value={record.facility_type}
                          onChange={(e) => updateFacilityRecord(index, 'facility_type', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Type</option>
                          <option value="command-post">Command Post</option>
                          <option value="base-camp">Base Camp</option>
                          <option value="staging-area">Staging Area</option>
                          <option value="helibase">Helibase</option>
                          <option value="medical">Medical Facility</option>
                          <option value="food-service">Food Service</option>
                          <option value="sleeping">Sleeping Quarters</option>
                          <option value="sanitation">Sanitation</option>
                          <option value="storage">Storage</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Location</Label>
                        <Input
                          value={record.location}
                          onChange={(e) => updateFacilityRecord(index, 'location', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Physical location/address"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Capacity</Label>
                        <Input
                          value={record.capacity}
                          onChange={(e) => updateFacilityRecord(index, 'capacity', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="e.g., 50 personnel"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Current Condition</Label>
                        <select
                          value={record.current_condition}
                          onChange={(e) => updateFacilityRecord(index, 'current_condition', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Condition</option>
                          <option value="excellent">Excellent</option>
                          <option value="good">Good</option>
                          <option value="fair">Fair</option>
                          <option value="poor">Poor</option>
                          <option value="needs-repair">Needs Repair</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Status</Label>
                        <select
                          value={record.status}
                          onChange={(e) => updateFacilityRecord(index, 'status', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Status</option>
                          <option value="planned">Planned</option>
                          <option value="setup-in-progress">Setup In Progress</option>
                          <option value="operational">Operational</option>
                          <option value="demob-in-progress">Demobilization In Progress</option>
                          <option value="demobilized">Demobilized</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Layout Notes</Label>
                        <Textarea
                          value={record.layout_notes}
                          onChange={(e) => updateFacilityRecord(index, 'layout_notes', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Layout, floor plan, special features..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Setup Date</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.setup_date ? record.setup_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.setup_date ? record.setup_date.split('T')[1] : '00:00';
                              updateFacilityRecord(index, 'setup_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength="5"
                            value={record.setup_date ? record.setup_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) value = value + ':';
                              if (value.length <= 5) {
                                const date = record.setup_date ? record.setup_date.split('T')[0] : '';
                                updateFacilityRecord(index, 'setup_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Demobilization Date</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.demobilization_date ? record.demobilization_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.demobilization_date ? record.demobilization_date.split('T')[1] : '00:00';
                              updateFacilityRecord(index, 'demobilization_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength="5"
                            value={record.demobilization_date ? record.demobilization_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) value = value + ':';
                              if (value.length <= 5) {
                                const date = record.demobilization_date ? record.demobilization_date.split('T')[0] : '';
                                updateFacilityRecord(index, 'demobilization_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => deleteFacilityRecord(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Facility
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Utilities Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Utilities & Services</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('utilities')}
              <Button onClick={() => saveCard('utilities')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Utility Records</Label>
              <Button onClick={addUtilityRecord} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Utility
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.utility_records.map((record, index) => (
                <Card key={record.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Utility Type</Label>
                        <select
                          value={record.utility_type}
                          onChange={(e) => updateUtilityRecord(index, 'utility_type', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Type</option>
                          <option value="water">Water</option>
                          <option value="power">Electrical Power</option>
                          <option value="sanitation">Sanitation</option>
                          <option value="internet">Internet</option>
                          <option value="phone">Telephone</option>
                          <option value="gas">Natural Gas</option>
                          <option value="hvac">HVAC</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Provider</Label>
                        <Input
                          value={record.provider}
                          onChange={(e) => updateUtilityRecord(index, 'provider', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Service provider name"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Availability</Label>
                        <select
                          value={record.availability}
                          onChange={(e) => updateUtilityRecord(index, 'availability', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select</option>
                          <option value="available">Available</option>
                          <option value="limited">Limited</option>
                          <option value="unavailable">Unavailable</option>
                          <option value="pending">Pending Connection</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Capacity</Label>
                        <Input
                          value={record.capacity}
                          onChange={(e) => updateUtilityRecord(index, 'capacity', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="e.g., 500 gallons/day, 100kW"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Connection Date</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.connection_date ? record.connection_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.connection_date ? record.connection_date.split('T')[1] : '00:00';
                              updateUtilityRecord(index, 'connection_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength="5"
                            value={record.connection_date ? record.connection_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) value = value + ':';
                              if (value.length <= 5) {
                                const date = record.connection_date ? record.connection_date.split('T')[0] : '';
                                updateUtilityRecord(index, 'connection_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Location</Label>
                        <Input
                          value={record.location}
                          onChange={(e) => updateUtilityRecord(index, 'location', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Connection point location"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Contact Information</Label>
                        <Input
                          value={record.contact_info}
                          onChange={(e) => updateUtilityRecord(index, 'contact_info', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Provider contact details"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Notes</Label>
                        <Textarea
                          value={record.notes}
                          onChange={(e) => updateUtilityRecord(index, 'notes', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Additional information..."
                          rows={2}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => deleteUtilityRecord(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Utility
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security Needs Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Security Requirements</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('security')}
              <Button onClick={() => saveCard('security')} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <Label className={`${theme.colors.textSecondary}`}>Security Records</Label>
              <Button onClick={addSecurityRecord} variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Security Measure
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.security_records.map((record, index) => (
                <Card key={record.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Security Measure</Label>
                        <Input
                          value={record.security_measure}
                          onChange={(e) => updateSecurityRecord(index, 'security_measure', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Type of security measure"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Location</Label>
                        <Input
                          value={record.location}
                          onChange={(e) => updateSecurityRecord(index, 'location', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Where implemented"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Responsible Person</Label>
                        <Input
                          value={record.responsible_person}
                          onChange={(e) => updateSecurityRecord(index, 'responsible_person', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Person responsible"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Status</Label>
                        <select
                          value={record.status}
                          onChange={(e) => updateSecurityRecord(index, 'status', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white`}
                        >
                          <option value="">Select Status</option>
                          <option value="planned">Planned</option>
                          <option value="in-progress">In Progress</option>
                          <option value="implemented">Implemented</option>
                          <option value="under-review">Under Review</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Implementation Date</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.implementation_date ? record.implementation_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.implementation_date ? record.implementation_date.split('T')[1] : '00:00';
                              updateSecurityRecord(index, 'implementation_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength="5"
                            value={record.implementation_date ? record.implementation_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) value = value + ':';
                              if (value.length <= 5) {
                                const date = record.implementation_date ? record.implementation_date.split('T')[0] : '';
                                updateSecurityRecord(index, 'implementation_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-orange-500 text-xs font-semibold">Review Date</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={record.review_date ? record.review_date.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = record.review_date ? record.review_date.split('T')[1] : '00:00';
                              updateSecurityRecord(index, 'review_date', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength="5"
                            value={record.review_date ? record.review_date.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) value = value + ':';
                              if (value.length <= 5) {
                                const date = record.review_date ? record.review_date.split('T')[0] : '';
                                updateSecurityRecord(index, 'review_date', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-orange-500 text-xs font-semibold">Notes</Label>
                        <Textarea
                          value={record.notes}
                          onChange={(e) => updateSecurityRecord(index, 'notes', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white`}
                          placeholder="Additional security information..."
                          rows={2}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => deleteSecurityRecord(index)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Security Record
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Prepared By Section */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Prepared By</CardTitle>
            <div className="flex items-center gap-2 print:hidden">
              {renderSaveStatus('prepared')}
              <Button onClick={() => saveCard('prepared')} disabled={loading} className="bg-green-600 hover:bg-green-700" size="sm">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Name"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Signature</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                  placeholder="Digital signature"
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date/Time Prepared</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.date_time_prepared ? formData.date_time_prepared.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_time_prepared ? formData.date_time_prepared.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, date_time_prepared: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_time_prepared ? formData.date_time_prepared.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_time_prepared ? formData.date_time_prepared.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, date_time_prepared: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end print:hidden">
          <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} Facilities Unit
          </Button>
        </div>
      </div>
    </div>
  );
};


// Compensation and Claims Form

export default FacilitiesUnitForm;
